import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Zoom from '@mui/material/Zoom';
import Fade from '@mui/material/Fade';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import type { FormValues } from '../../agent/types';
import { MOCK_FORM_DATA, TEMPLATE_FILL_COMMANDS } from '../data/mockFormData';

interface Message {
  id: number;
  role: 'agent' | 'user';
  text: string;
  timestamp: Date;
}

interface AgentChatWidgetProps {
  templateId: string | null;
  templateName: string | null;
  onFillForm: (values: FormValues) => void;
}

const WELCOME_MESSAGE = (templateName: string | null): string => {
  if (!templateName) {
    return 'Olá! Selecione um template para começar. Depois posso te ajudar a preencher o formulário automaticamente.';
  }
  return `Olá! Estou pronto para te ajudar com o template **${templateName}**.\n\nDigite o nome do template (ex: \`rebaixa-simples\`) para que eu preencha o formulário automaticamente com dados de exemplo.`;
};

const FILL_COMMANDS_HINT = (templateId: string | null): string[] => {
  if (!templateId) return [];
  return TEMPLATE_FILL_COMMANDS[templateId] ?? [];
};

function getAgentReply(
  userText: string,
  templateId: string | null,
  onFill: (values: FormValues) => void,
): string {
  const normalized = userText.trim().toLowerCase();

  if (!templateId) {
    return 'Nenhum template selecionado. Por favor, escolha um template primeiro.';
  }

  let matchedTid: string | null = null;
  for (const [tid, cmds] of Object.entries(TEMPLATE_FILL_COMMANDS)) {
    if (cmds.some((c) => normalized.includes(c))) {
      matchedTid = tid;
      break;
    }
  }

  if (matchedTid !== null) {
    if (matchedTid !== templateId) {
      const currentCmds = TEMPLATE_FILL_COMMANDS[templateId] ?? [];
      return `Este formulário é para o template atual. Tente digitar \`${currentCmds[0] ?? templateId}\` para preenchê-lo.`;
    }
    const mockData = MOCK_FORM_DATA[templateId];
    if (mockData) {
      onFill(mockData);
      return `Pronto! Preenchi o formulário com dados de exemplo para o template atual. Você pode revisar e ajustar os campos conforme necessário.`;
    }
  }

  if (normalized.includes('ajuda') || normalized.includes('help') || normalized.includes('como')) {
    const cmds = FILL_COMMANDS_HINT(templateId);
    return `Para preencher o formulário automaticamente, envie uma mensagem com:\n${cmds.map((c) => `• \`${c}\``).join('\n')}\n\nAssim irei preencher todos os campos com dados de exemplo.`;
  }

  if (normalized.includes('limpar') || normalized.includes('clear') || normalized.includes('reset')) {
    return 'Para limpar o formulário, use o botão "Limpar" ou "Limpar formulário" dentro do form.';
  }

  if (normalized.includes('exportar') || normalized.includes('xlsx') || normalized.includes('excel')) {
    return 'Para exportar o formulário em XLSX, use o botão "Exportar XLSX" ao final do formulário.';
  }

  const cmds = FILL_COMMANDS_HINT(templateId);
  return `Não entendi. Tente:\n• \`${cmds[0] ?? 'rebaixa-simples'}\` — para preencher o formulário\n• \`ajuda\` — para ver os comandos disponíveis`;
}

let msgIdCounter = 0;
function makeMsg(role: 'agent' | 'user', text: string): Message {
  return { id: ++msgIdCounter, role, text, timestamp: new Date() };
}

export default function AgentChatWidget({ templateId, templateName, onFillForm }: AgentChatWidgetProps) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset conversation when template changes
  useEffect(() => {
    setMessages([makeMsg('agent', WELCOME_MESSAGE(templateName))]);
  }, [templateId, templateName]);

  useEffect(() => {
    if (open) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  const sendMessage = () => {
    const text = input.trim();
    if (!text) return;

    setInput('');
    setMessages((prev) => [...prev, makeMsg('user', text)]);
    setIsTyping(true);

    // Simulate agent "thinking"
    setTimeout(() => {
      const reply = getAgentReply(text, templateId, onFillForm);
      setMessages((prev) => [...prev, makeMsg('agent', reply)]);
      setIsTyping(false);
    }, 600);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const hints = FILL_COMMANDS_HINT(templateId);

  return (
    <>
      {/* Chat panel */}
      <Zoom in={open} unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 88,
            right: 24,
            width: 360,
            height: 480,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: 1400,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'grey.900',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16, color: '#fff' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
                Agente IA
              </Typography>
              <Typography variant="caption" sx={{ color: 'grey.400', lineHeight: 1 }}>
                {templateName ? `Template: ${templateName}` : 'Nenhum template ativo'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'grey.400', '&:hover': { color: '#fff' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            {messages.map((msg) => (
              <Fade in key={msg.id} timeout={200}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-end',
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: msg.role === 'agent' ? 'primary.main' : 'grey.300',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {msg.role === 'agent' ? (
                      <SmartToyOutlinedIcon sx={{ fontSize: 14, color: '#fff' }} />
                    ) : (
                      <PersonOutlineIcon sx={{ fontSize: 14, color: 'grey.700' }} />
                    )}
                  </Box>

                  {/* Bubble */}
                  <Box
                    sx={{
                      maxWidth: '78%',
                      px: 1.5,
                      py: 1,
                      borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                      bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                      boxShadow: 1,
                      border: msg.role === 'agent' ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: msg.role === 'user' ? '#fff' : 'text.primary',
                        whiteSpace: 'pre-line',
                        lineHeight: 1.5,
                        fontSize: '0.8125rem',
                        '& code': {
                          bgcolor: msg.role === 'user' ? 'rgba(255,255,255,0.2)' : 'grey.100',
                          px: 0.5,
                          borderRadius: 0.5,
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: msg.text
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/`(.*?)`/g, '<code>$1</code>'),
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        color: msg.role === 'user' ? 'rgba(255,255,255,0.6)' : 'text.disabled',
                        fontSize: '0.65rem',
                        textAlign: msg.role === 'user' ? 'right' : 'left',
                      }}
                    >
                      {msg.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <Fade in>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: '50%',
                      bgcolor: 'primary.main', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    <SmartToyOutlinedIcon sx={{ fontSize: 14, color: '#fff' }} />
                  </Box>
                  <Box
                    sx={{
                      px: 1.5, py: 1, borderRadius: '12px 12px 12px 2px',
                      bgcolor: 'background.paper', boxShadow: 1,
                      border: '1px solid', borderColor: 'divider',
                      display: 'flex', gap: 0.5, alignItems: 'center',
                    }}
                  >
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 6, height: 6, borderRadius: '50%',
                          bgcolor: 'grey.400',
                          animation: 'bounce 1.2s ease-in-out infinite',
                          animationDelay: `${i * 0.2}s`,
                          '@keyframes bounce': {
                            '0%, 80%, 100%': { transform: 'scale(0.8)', opacity: 0.5 },
                            '40%': { transform: 'scale(1)', opacity: 1 },
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Fade>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Quick hints */}
          {hints.length > 0 && (
            <Box
              sx={{
                px: 2, py: 1,
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 0.75,
                flexWrap: 'wrap',
                flexShrink: 0,
              }}
            >
              {hints.slice(0, 3).map((hint) => (
                <Chip
                  key={hint}
                  label={hint}
                  size="small"
                  icon={<AutoFixHighIcon sx={{ fontSize: '12px !important' }} />}
                  onClick={() => {
                    setInput(hint);
                    inputRef.current?.focus();
                  }}
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    fontFamily: 'monospace',
                    bgcolor: 'grey.100',
                    '&:hover': { bgcolor: 'primary.50' },
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Box>
          )}

          {/* Input */}
          <Box
            sx={{
              px: 1.5, py: 1.25,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              gap: 1,
              alignItems: 'flex-end',
              flexShrink: 0,
            }}
          >
            <TextField
              inputRef={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite uma mensagem..."
              multiline
              maxRows={3}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.8125rem',
                },
              }}
            />
            <IconButton
              onClick={sendMessage}
              disabled={!input.trim()}
              color="primary"
              size="small"
              sx={{
                bgcolor: input.trim() ? 'primary.main' : 'grey.200',
                color: input.trim() ? '#fff' : 'grey.400',
                '&:hover': { bgcolor: input.trim() ? 'primary.dark' : 'grey.300' },
                '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' },
                width: 36, height: 36,
                flexShrink: 0,
                transition: 'all 0.2s',
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Paper>
      </Zoom>

      {/* FAB */}
      <Tooltip title="Agente IA" placement="left">
        <Fab
          color="primary"
          onClick={() => setOpen((v) => !v)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1400,
            boxShadow: 6,
            transition: 'transform 0.2s',
            '&:hover': { transform: 'scale(1.08)' },
          }}
        >
          {open ? (
            <CloseIcon />
          ) : (
            <AutoAwesomeIcon />
          )}
        </Fab>
      </Tooltip>
    </>
  );
}
